import React, { useState, useCallback, useEffect } from 'react';
import AnimatedScatterPlot from '../components/AnimatedScatterPlot';
import { PaymentButton } from '../components/PaymentButton';
import { PaymentStatus } from '../components/PaymentStatus';

interface ScatterPoint {
  x: number;
  y: number;
  label: string;
}

interface ScatterFrame {
  timestamp: string;
  points: ScatterPoint[];
}

const AnimatedScatterPlotPage: React.FC = () => {
  const [xDataText, setXDataText] = useState('');
  const [yDataText, setYDataText] = useState('');
  const [data, setData] = useState<ScatterFrame[]>([]);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [shouldDraw, setShouldDraw] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [showWatermark, setShowWatermark] = useState(true);
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [labelFontSize, setLabelFontSize] = useState(14);
  const [isPaid, setIsPaid] = useState(localStorage.getItem('isPaid') === 'true');
  const [remainingDraws, setRemainingDraws] = useState(
    parseInt(localStorage.getItem('remainingDraws') || '0', 10)
  );
  const [hasExpired, setHasExpired] = useState(localStorage.getItem('hasExpired') === 'true');
  const [drawCount, setDrawCount] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Data validation function
  const validateData = (xText: string, yText: string): { isValid: boolean; error?: string } => {
    const xLines = xText.trim().split('\n');
    const yLines = yText.trim().split('\n');
    
    // Check if we have at least headers and one data row
    if (xLines.length < 2) {
      return { isValid: false, error: 'X table must have at least a header row and one data row.' };
    }
    if (yLines.length < 2) {
      return { isValid: false, error: 'Y table must have at least a header row and one data row.' };
    }

    // Check row count match
    if (xLines.length !== yLines.length) {
      return { 
        isValid: false, 
        error: `Row count mismatch: X table has ${xLines.length} rows, Y table has ${yLines.length} rows.` 
      };
    }

    // Parse headers
    const xHeaders = xLines[0].split(/[\t,]+/).filter(val => val.trim() !== '');
    const yHeaders = yLines[0].split(/[\t,]+/).filter(val => val.trim() !== '');
    
    // Check column count match
    if (xHeaders.length !== yHeaders.length) {
      return { 
        isValid: false, 
        error: `Column count mismatch: X table has ${xHeaders.length} columns, Y table has ${yHeaders.length} columns.` 
      };
    }

    // Check that labels match (except first column which can be anything)
    for (let i = 1; i < xHeaders.length; i++) {
      if (xHeaders[i] !== yHeaders[i]) {
        return { 
          isValid: false, 
          error: `Label mismatch in column ${i + 1}: X table has "${xHeaders[i]}", Y table has "${yHeaders[i]}".` 
        };
      }
    }

    return { isValid: true };
  };

  const parseData = (xText: string, yText: string): ScatterFrame[] => {
    const xLines = xText.trim().split('\n');
    const yLines = yText.trim().split('\n');
    
    if (xLines.length < 2 || yLines.length < 2) {
      return [];
    }

    // Parse headers (first row)
    const xHeaders = xLines[0].split(/[\t,]+/).filter(val => val.trim() !== '');
    const yHeaders = yLines[0].split(/[\t,]+/).filter(val => val.trim() !== '');
    
    // Remove header rows
    const xDataRows = xLines.slice(1);
    const yDataRows = yLines.slice(1);

    const frames: ScatterFrame[] = [];

    // Process each data row
    xDataRows.forEach((xRow, rowIndex) => {
      const yRow = yDataRows[rowIndex];
      if (!yRow) return;

      const xValues = xRow.split(/[\t,]+/).filter(val => val.trim() !== '');
      const yValues = yRow.split(/[\t,]+/).filter(val => val.trim() !== '');

      if (xValues.length < 2 || yValues.length < 2) return;

      const timestamp = xValues[0]; // Use X timestamp
      const points: ScatterPoint[] = [];

      // Match X and Y values by their column index
      for (let i = 1; i < Math.min(xValues.length, yValues.length); i++) {
        const xVal = parseFloat(xValues[i]);
        const yVal = parseFloat(yValues[i]);
        
        if (!isNaN(xVal) && !isNaN(yVal)) {
          const label = xHeaders[i] || yHeaders[i] || `Point ${i}`;
          points.push({
            x: xVal,
            y: yVal,
            label: label
          });
        }
      }

      if (points.length > 0) {
        frames.push({
          timestamp,
          points
        });
      }
    });

    return frames;
  };

  const handleDrawGraph = () => {
    // Clear any previous validation errors
    setValidationError(null);
    
    // Validate data first
    const validation = validateData(xDataText, yDataText);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Data validation failed.');
      return;
    }

    const parsedData = parseData(xDataText, yDataText);
    setData(parsedData);
    setShouldDraw(true);
  };

  const handleUseExampleData = async () => {
    try {
      const [xResponse, yResponse] = await Promise.all([
        fetch('/exampleScatterXData.txt'),
        fetch('/exampleScatterYData.txt')
      ]);
      
      if (!xResponse.ok || !yResponse.ok) {
        throw new Error('Failed to load example data');
      }
      
      const xText = await xResponse.text();
      const yText = await yResponse.text();
      
      setXDataText(xText);
      setYDataText(yText);
    } catch (error) {
      console.error('Error loading example data:', error);
      // Fallback example data
      const fallbackXData = `Year\tGDP\tPopulation\tExports
2020\t21.4\t331.0\t2.1
2021\t23.3\t332.0\t2.3
2022\t25.5\t333.0\t2.5
2023\t26.9\t334.0\t2.7`;

      const fallbackYData = `Year\tGDP\tPopulation\tExports
2020\t21.4\t331.0\t2.1
2021\t23.3\t332.0\t2.3
2022\t25.5\t333.0\t2.5
2023\t26.9\t334.0\t2.7`;

      setXDataText(fallbackXData);
      setYDataText(fallbackYData);
    }
  };

  const stopRecording = useCallback(() => {
    setIsRecording(false);
  }, []);

  // Payment state sync
  useEffect(() => {
    setIsPaid(localStorage.getItem('isPaid') === 'true');
    setRemainingDraws(parseInt(localStorage.getItem('remainingDraws') || '0', 10));
    setHasExpired(localStorage.getItem('hasExpired') === 'true');
  }, []);

  // When payment is made, update state
  const handlePaymentSuccess = useCallback(() => {
    setIsPaid(true);
    setRemainingDraws(5);
    setHasExpired(false);
    localStorage.setItem('isPaid', 'true');
    localStorage.setItem('remainingDraws', '5');
    localStorage.setItem('hasExpired', 'false');
  }, []);

  // On each animation, decrement remainingDraws if paid
  const handleAnimationComplete = useCallback(() => {
    setShouldDraw(false);
    if (isPaid && remainingDraws > 0) {
      const newDraws = remainingDraws - 1;
      setRemainingDraws(newDraws);
      setDrawCount(drawCount + 1);
      localStorage.setItem('remainingDraws', newDraws.toString());
      if (newDraws <= 0) {
        setIsPaid(false);
        setHasExpired(true);
        localStorage.setItem('isPaid', 'false');
        localStorage.setItem('hasExpired', 'true');
      }
    }
  }, [isPaid, remainingDraws, drawCount]);

  // Payment expiration callback
  const handlePaymentExpire = useCallback(() => {
    setIsPaid(false);
    setHasExpired(true);
    localStorage.setItem('isPaid', 'false');
    localStorage.setItem('hasExpired', 'true');
  }, []);

  // Watermark logic: only remove if paid and draws remain
  const watermarkRemoved = isPaid && remainingDraws > 0 && !hasExpired;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  const handleVideoDownload = () => {
    // Clear any previous validation errors
    setValidationError(null);
    
    // Validate data first
    const validation = validateData(xDataText, yDataText);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Data validation failed.');
      return;
    }

    // Parse and set the data first
    const parsedData = parseData(xDataText, yDataText);
    setData(parsedData);
    
    // Start recording and drawing
    setIsRecording(true);
    setShouldDraw(true);
    
    if (isIOS && isSafari) {
      // For iOS Safari, trigger the share sheet after a delay
      setTimeout(() => {
        if (navigator.share) {
          navigator.share({
            title: 'Animated Scatter Plot',
            text: 'Check out this animated scatter plot!'
          }).catch(console.error);
        }
      }, 2000);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Animated XY Scatter Plot</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <p>
          Enter your data in two tables below. The first table contains X values, and the second table contains Y values.
          Each table should have timestamps in the first column and data labels in the first row.
        </p>
        <button 
          onClick={handleUseExampleData}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '10px'
          }}
        >
          Use Example Data
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <h3>X Values Table</h3>
          <textarea
            value={xDataText}
            onChange={(e) => setXDataText(e.target.value)}
            placeholder="Enter X values data here...
Format: Tab or comma separated
First row: Labels
First column: Timestamps
Example:
Year	GDP	Population	Exports
2020	21.4	331.0	2.1
2021	23.3	332.0	2.3"
            style={{
              width: '100%',
              height: '200px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
            className="mobile-textarea"
          />
        </div>
        
        <div>
          <h3>Y Values Table</h3>
          <textarea
            value={yDataText}
            onChange={(e) => setYDataText(e.target.value)}
            placeholder="Enter Y values data here...
Format: Tab or comma separated
First row: Labels
First column: Timestamps
Example:
Year	GDP	Population	Exports
2020	21.4	331.0	2.1
2021	23.3	332.0	2.3"
            style={{
              width: '100%',
              height: '200px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
            className="mobile-textarea"
          />
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
          <div>
            <label>Animation Speed: </label>
            <input
              type="range"
              min="0.05"
              max="2.0"
              step="0.05"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              style={{ marginLeft: '10px' }}
              className="mobile-slider"
            />
            <span style={{ marginLeft: '10px' }}>{animationSpeed.toFixed(2)}x</span>
          </div>
          
          <div>
            <label>Background Color: </label>
            <select
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="mobile-select"
            >
              <option value="#ffffff">White</option>
              <option value="#404040">Gray</option>
              <option value="black">Black</option>
            </select>
          </div>
          
          <div>
            <label>
              <input
                type="checkbox"
                checked={showWatermark}
                onChange={(e) => setShowWatermark(e.target.checked)}
                className="mobile-checkbox"
              />
              Show Watermark
            </label>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
          <div>
            <label>X-Axis Label: </label>
            <input
              type="text"
              value={xAxisLabel}
              onChange={(e) => setXAxisLabel(e.target.value)}
              placeholder="Enter X-axis label..."
              style={{ marginLeft: '10px', padding: '5px', borderRadius: '3px', border: '1px solid #ccc' }}
              className="mobile-input"
            />
          </div>
          
          <div>
            <label>Y-Axis Label: </label>
            <input
              type="text"
              value={yAxisLabel}
              onChange={(e) => setYAxisLabel(e.target.value)}
              placeholder="Enter Y-axis label..."
              style={{ marginLeft: '10px', padding: '5px', borderRadius: '3px', border: '1px solid #ccc' }}
              className="mobile-input"
            />
          </div>
          
          <div>
            <label>Label Font Size: </label>
            <input
              type="range"
              min="8"
              max="32"
              step="1"
              value={labelFontSize}
              onChange={(e) => setLabelFontSize(parseInt(e.target.value, 10))}
              style={{ marginLeft: '10px' }}
              className="mobile-slider"
            />
            <span style={{ marginLeft: '10px' }}>{labelFontSize}px</span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={handleDrawGraph}
          disabled={!xDataText.trim() || !yDataText.trim()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Draw Graph
        </button>
        
        <button
          onClick={handleVideoDownload}
          disabled={!xDataText.trim() || !yDataText.trim()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Draw Graph and Capture Video
        </button>
      </div>

      {data.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <p>Frames: {data.length} | Points per frame: {data[0]?.points.length || 0}</p>
        </div>
      )}

      {validationError && (
        <div style={{ color: 'red', marginBottom: '20px' }}>
          {validationError}
        </div>
      )}

      <AnimatedScatterPlot
        data={data}
        animationSpeed={animationSpeed}
        shouldDraw={shouldDraw}
        isRecording={isRecording}
        stopRecording={stopRecording}
        backgroundColor={backgroundColor}
        showWatermark={!watermarkRemoved}
        xAxisLabel={xAxisLabel}
        yAxisLabel={yAxisLabel}
        labelFontSize={labelFontSize}
        onAnimationComplete={handleAnimationComplete}
      />

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <h3 style={{ marginTop: '0', marginBottom: '15px' }}>Remove Watermark</h3>
        <PaymentButton 
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentExpire={handlePaymentExpire}
          shouldDraw={shouldDraw}
          drawCount={drawCount}
        />
        <PaymentStatus 
          remainingDraws={remainingDraws}
          hasExpired={hasExpired}
        />
      </div>
    </div>
  );
};

export default AnimatedScatterPlotPage; 